<template>
  <teleport to="body">
    <transition name="fade">
      <div
        class="fixed top-0 left-0 px-15 pt-70 w-full h-full bg-backdrop z-9999 md:z-100"
        @click.self="close"
      >
        <div class="flex flex-col max-h-full h-full relative overflow-y-auto pt-50 pb-20 px-15 md:px-100 bg-primary mx-auto max-w-1020 w-full rounded-t-4 text-app-color">
          <p v-if="autoSavedAt" class="absolute right-20 top-15 text-14 md:text-15 text-grey-300">Auto-saved at {{ parseDate(autoSavedAt, 'hh:mm') }}</p>
          <div class="flex items-center">
            <p class="w-75 md:w-100 font-semibold text-15">Post in</p>
            <SelectedBookModal ref="selectedBookModalRef" :books="allBooks" :selected-book="localSelectedBook" @toggleBook="toggleBook" />
          </div>
          <div class="flex items-center mt-10">
            <p class="w-75 md:w-100 font-semibold text-15">Mention</p>

            <DiscussionMention :members="members" :selected-members="selectedMembers" @toggleMember="toggleMember" />
          </div>
          <!-- title -->
          <input
            id="title"
            ref="titleInputRef"
            :value="discussion.title"
            placeholder="Title"
            class="w-full mt-40 text-35 bg-transparent outline-none font-bold leading-small block"
            type="text"
            @input="updateTitle"
          >
          <!-- content -->
          <div class="flex-grow mb-20 mt-10">
            <DiscussionEditor
              :discussion="discussion"
              :book-id="localSelectedBook.id"
              @updateContent="updateContent"
            />
          </div>

          <div class="flex justify-end">
            <ButtonModule
              text="Create discussion"
              class="bg-brand hover:bg-brand-hover text-white rounded-8 font-medium leading-none py-13 px-50 md:px-140 mx-auto"
              @click="post"
            />
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'

import { parseDate } from '@/plugins/filters'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import DiscussionEditor from '@/components/discussions/DiscussionEditor'
import DiscussionMention from '@/components/discussions/DiscussionMention'
import SelectedBookModal from '@/components/task-board/SelectedBookModal'

export default defineComponent({
  name: 'DiscussionModal',

  components: {
    ButtonModule,
    DiscussionEditor,
    DiscussionMention,
    SelectedBookModal
  },

  props: {
    selectedBook: Object
  },

  emits: ['close', 'updateDiscussion'],

  setup (props, { emit }) {
    const { state, commit, getters, dispatch } = useStore()

    const user = computed(() => state.profile.user)

    const companyName = computed(() => state.team.team.name)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    // const books = computed(() => state.book.books.filter(b => !b.isSection))
    const headquarters = computed(() => state.team.headquarters)
    const teamMembers = computed(() => state.team.members)

    const allBooks = computed(() => {
      const result = state.book.books.filter(b => !b.isSection)
      result.push(headquarters.value)
      return result
    })

    const members = computed(() => {
      if (props.selectedBook.id === headquarters.value.id) return teamMembers.value

      return props.selectedBook.shareWith
    })

    const selectedMembers = ref([])

    const localSelectedBook = ref(props.selectedBook)

    const countMessages = computed(() => state.chat.taskMessages?.length)

    const attachments = ref(0)
    const title = ref()
    const autoSavedAt = ref(null)

    const discussion = ref({
      title: ''
    })

    function toggleMember ({ member, isSelected }) {
      if (isSelected) {
        selectedMembers.value = selectedMembers.value.filter(item => item.id !== member.id)
      } else {
        selectedMembers.value.push(member)
      }
    }

    function close () {
      emit('close')
    }

    let timer = null

    function updateTitle (e) {
      clearTimeout(timer)
      if (e.target.value.trim() === '') return

      timer = setTimeout(() => {
        discussion.value.title = e.target.value
        if (discussion.value.title.trim() !== '' && !discussion.value.id) {
          createDiscussion()
        } else {
          save()
        }
      }, 300)
    }

    function createDiscussion () {
      autoSavedAt.value = new Date()
      dispatch('createDiscussion', {
        title: discussion.value.title,
        content: discussion.value.content,
        bookId: localSelectedBook.value.id
      }).then(res => {
        commit('addDraftDiscussion', {
          ...res.data,
          user: user.value
        })
        discussion.value = res.data
      })
    }

    function updateContent (value) {
      discussion.value.content = value
      save()
    }

    let saveTimer = null

    function save () {
      clearTimeout(saveTimer)

      if (discussion.value.id) {
        saveTimer = setTimeout(() => {
          autoSavedAt.value = new Date()
          dispatch('updateDiscussion', {
            id: discussion.value.id,
            title: discussion.value.title,
            content: discussion.value.content,
            bookId: discussion.value.bookId,
            newBookId: localSelectedBook.value.id
          })
        }
        , 400)
      }
    }

    function toggleBook (book) {
      localSelectedBook.value = book
    }

    async function post () {
      if (discussion.value.title.trim() !== '') {
        await dispatch('postDiscussion', {
          id: discussion.value.id,
          bookId: localSelectedBook.value.id,
          memberIds: selectedMembers.value.map(item => item.id)
        })
      }
      close()
    }

    return {
      // refs
      // data
      title,
      discussion,
      autoSavedAt,
      // computed
      user,
      members,
      allBooks,
      attachments,
      companyName,
      countMessages,
      isTeamAccount,
      selectedMembers,
      localSelectedBook,
      // function
      post,
      save,
      close,
      parseDate,
      toggleBook,
      updateTitle,
      toggleMember,
      updateContent
    }
  }
})
</script>
